import { apiFetch } from './http';

// ─────────────────────────────────────────────────────────────────────
// MY OWN HISTORY, ACROSS EVERY CLUB (#760).
//
// `GET /me/bookings`, `/me/activities` and `/me/matches` — account-scoped
// reads keyed on the PERSON. No club is named in the request: the server finds
// the player from the session and answers with that person's own rows from
// every club, each labelled with the club it belongs to.
//
// Distinct from the club's own lists (`/member/bootstrap`), which are scoped to
// the active club and still drive «Reservasjoner» and «Kamper» exactly as
// before. This is the «alle klubber» view beside them.
//
// «NONE» AND «WE COULD NOT ASK» STAY DIFFERENT ANSWERS, as `fetchMyClubs` keeps
// them: an empty list is the server saying there is nothing; `failed` is the
// question going unanswered, and the screen offers a retry instead of «you
// have none».
// ─────────────────────────────────────────────────────────────────────

export interface HistoryClub {
  slug: string;
  name: string;
}

export interface MyHistoryBooking {
  id: string;
  club: HistoryClub;
  court: number;
  /** Local `YYYY-MM-DD`. */
  date: string;
  startTime: string;
  endTime: string | null;
  title: string | null;
  paymentStatus: string;
  priceNok: number | null;
}

export interface MyHistoryActivity {
  id: string;
  club: HistoryClub;
  name: string | null;
  status: string;
  when: { date: string | null; start: string | null; end: string | null; days: number };
  registration: { status: string; asPartner: boolean; paidStatus: string | null };
}

export interface MyHistoryMatch {
  id: string;
  club: HistoryClub;
  playedAt: number;
  format: string;
  partner: { name: string } | null;
  opponents: { name: string }[];
  sets: { mine: number; theirs: number }[];
  won: boolean | null;
  eloChange: number | null;
}

export type HistoryResult<T> = { items: T[]; failed: boolean };

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);
const s = (v: unknown): string => (typeof v === 'string' ? v : '');
const n = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** A club label, or null — a row the screen cannot attribute is dropped. */
function clubOf(v: unknown): HistoryClub | null {
  if (!isObj(v) || typeof v.slug !== 'string' || typeof v.name !== 'string') return null;
  return { slug: v.slug, name: v.name };
}

async function read<T>(
  path: string,
  key: string,
  shape: (row: Obj) => T | null,
): Promise<HistoryResult<T>> {
  let res: Awaited<ReturnType<typeof apiFetch>>;
  try {
    res = await apiFetch(path);
  } catch {
    return { items: [], failed: true };
  }
  if (!res.ok) return { items: [], failed: true };
  const body = (await res.json().catch(() => null)) as Obj | null;
  const rows = body?.[key];
  if (!Array.isArray(rows)) return { items: [], failed: true };
  // SHAPED, NOT TRUSTED — the same rule `fetchMyClubs` applies.
  const items: T[] = [];
  for (const row of rows) {
    if (!isObj(row)) continue;
    const item = shape(row);
    if (item) items.push(item);
  }
  return { items, failed: false };
}

export function fetchMyBookings(): Promise<HistoryResult<MyHistoryBooking>> {
  return read('/me/bookings', 'bookings', (r) => {
    const club = clubOf(r.club);
    if (!club || !s(r.id) || !s(r.date)) return null;
    return {
      id: s(r.id),
      club,
      court: n(r.court) ?? 0,
      date: s(r.date),
      startTime: s(r.startTime),
      endTime: s(r.endTime) || null,
      title: s(r.title) || null,
      paymentStatus: s(r.paymentStatus) || 'free',
      priceNok: n(r.priceNok),
    };
  });
}

export function fetchMyActivities(): Promise<HistoryResult<MyHistoryActivity>> {
  return read('/me/activities', 'activities', (r) => {
    const club = clubOf(r.club);
    if (!club || !s(r.id)) return null;
    const w = isObj(r.when) ? r.when : {};
    const reg = isObj(r.registration) ? r.registration : {};
    return {
      id: s(r.id),
      club,
      name: s(r.name) || null,
      status: s(r.status),
      when: {
        date: s(w.date) || null,
        start: s(w.start) || null,
        end: s(w.end) || null,
        days: n(w.days) ?? 0,
      },
      registration: {
        status: s(reg.status) || 'confirmed',
        asPartner: reg.asPartner === true,
        paidStatus: s(reg.paidStatus) || null,
      },
    };
  });
}

export function fetchMyMatches(): Promise<HistoryResult<MyHistoryMatch>> {
  return read('/me/matches', 'matches', (r) => {
    const club = clubOf(r.club);
    if (!club || !s(r.id)) return null;
    const named = (v: unknown) => (isObj(v) ? { name: s(v.name) } : null);
    return {
      id: s(r.id),
      club,
      playedAt: n(r.playedAt) ?? 0,
      format: s(r.format),
      partner: named(r.partner),
      opponents: (Array.isArray(r.opponents) ? r.opponents : [])
        .map(named)
        .filter((o): o is { name: string } => o != null),
      sets: (Array.isArray(r.sets) ? r.sets : [])
        .filter(isObj)
        .map((x) => ({ mine: n(x.mine), theirs: n(x.theirs) }))
        .filter((x): x is { mine: number; theirs: number } => x.mine != null && x.theirs != null),
      won: typeof r.won === 'boolean' ? r.won : null,
      eloChange: n(r.eloChange),
    };
  });
}
